import { useInfiniteQuery } from "@tanstack/react-query";
import { DocumentSnapshot } from "firebase/firestore";
import { queryClient } from "shared/api/queryClient";
import {
  getModulesListByBrandPage,
  ModulesListResult,
} from "../api/getModulesListByBrandPage";
import { PageModuleData } from "../types";

export const getQueryKey = (brandId: string, pageId: string): any => {
  return ["pageModules", { brandId, pageId }];
};

type QueryResult = {
  modules: PageModuleData[];
  hasNextPage: boolean;
};

export const useModuleListByBrandPageQuery = (
  brandId: string,
  pageId: string,
  pageSize: number = 20,
) => {
  return useInfiniteQuery<
    ModulesListResult,
    Error,
    QueryResult,
    string[],
    DocumentSnapshot | null
  >({
    queryKey: getQueryKey(brandId, pageId),
    queryFn: ({ pageParam }) =>
      getModulesListByBrandPage(brandId, pageId, pageSize, pageParam),
    initialPageParam: null,
    getNextPageParam: (lastPage) =>
      lastPage.hasMore ? lastPage.lastVisible : null,
    select: (data) => {
      const allModules = data.pages.flatMap((page) => page.modules);
      return {
        modules: allModules,
        hasNextPage: data.pages[data.pages.length - 1].hasMore,
      };
    },
  });
};

export const getModuleListByBranchPageQueryData = (
  brandId: string,
  pageId: string,
) => {
  const data = queryClient.getQueryData<{
    pages: ModulesListResult[];
    pageParams: any[];
  }>(getQueryKey(brandId, pageId));

  if (!data) return null;

  return data.pages.flatMap((page) => page.modules);
};

export const setModuleListByBranchPageQueryData = (
  brandId: string,
  pageId: string,
  data: PageModuleData[],
) => {
  const currentData = queryClient.getQueryData<{
    pages: ModulesListResult[];
    pageParams: any[];
  }>(getQueryKey(brandId, pageId));

  if (!currentData || currentData.pages.length === 0) {
    queryClient.setQueryData(getQueryKey(brandId, pageId), {
      pages: [{ modules: data, lastVisible: null, hasMore: false }],
      pageParams: [null],
    });
    return;
  }

  const updatedPages = [...currentData.pages];
  updatedPages[0] = {
    ...updatedPages[0],
    modules: data,
  };

  queryClient.setQueryData(getQueryKey(brandId, pageId), {
    ...currentData,
    pages: updatedPages,
  });
};

export const invalidateModuleListByBrandPageQuery = async (
  brandId: string,
  pageId: string,
) => {
  await queryClient.invalidateQueries({
    queryKey: getQueryKey(brandId, pageId),
  });
};
